import { InjectionToken, ModuleWithProviders, NgModule } from '@angular/core';
import { AiKnowledgeConfig, RouteCommand } from './interface/config.interface';
import { AiKnowledgeService } from './services/ai-knowledge.service';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  ACCOUNT_GROUPS_LOOKUP_TOKEN,
  AI_KNOWLEDGE_CONFIG_TOKEN,
  BUSINESS_PROFILE_URL_TOKEN,
  CAN_SHARE_TOKEN,
  CAN_SHARE_WITH_ACCOUNT_GROUPS_TOKEN,
  CAN_SHARE_WITH_PARTNERS_TOKEN,
  EDITING_ENABLED_TOKEN,
  KNOWLEDGE_GRANTS_ENABLED_TOKEN,
  MANAGE_KNOWLEDGE_URL_TOKEN,
  MARKET_ID_TOKEN,
  MULTI_LOCATION_GROUPS_LOOKUP_TOKEN,
  PARTNER_ID_TOKEN,
  PARTNER_NAME_RESOLVER_TOKEN,
  PARTNERS_LOOKUP_TOKEN,
  PartnerNameResolver,
  SHOW_BUSINESS_PROFILE_SOURCE_TOKEN,
  TargetLookup,
  WEB_CHAT_WIDGET_EDIT_ROUTE_TOKEN,
} from './tokens';
import { combineLatest, isObservable, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { KnowledgeIcon } from './assets/avatar-svg-string';

@NgModule()
export class AiKnowledgeModule {
  static forRoot(c: { config: InjectionToken<AiKnowledgeConfig> }): ModuleWithProviders<AiKnowledgeModule> {
    const result = {
      ngModule: AiKnowledgeModule,
      providers: [
        AiKnowledgeService,
        { provide: AI_KNOWLEDGE_CONFIG_TOKEN, useExisting: c.config },
        { provide: ACCOUNT_GROUP_ID_TOKEN, deps: [AI_KNOWLEDGE_CONFIG_TOKEN], useFactory: accountGroupIdFactory },
        { provide: MARKET_ID_TOKEN, deps: [AI_KNOWLEDGE_CONFIG_TOKEN], useFactory: marketIdFactory },
        { provide: PARTNER_ID_TOKEN, deps: [AI_KNOWLEDGE_CONFIG_TOKEN], useFactory: partnerIdFactory },
        {
          provide: MANAGE_KNOWLEDGE_URL_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: manageKnowledgeUrlFactory,
        },
        {
          provide: BUSINESS_PROFILE_URL_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: businessProfileUrlFactory,
        },
        {
          provide: SHOW_BUSINESS_PROFILE_SOURCE_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: showBusinessProfileSourceFactory,
        },
        {
          provide: WEB_CHAT_WIDGET_EDIT_ROUTE_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: webChatWidgetEditRouteFactory,
        },
        {
          provide: EDITING_ENABLED_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: editingEnabledFactory,
        },
        {
          provide: KNOWLEDGE_GRANTS_ENABLED_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: knowledgeGrantsEnabledFactory,
        },
        {
          provide: CAN_SHARE_WITH_PARTNERS_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: canShareWithPartnersFactory,
        },
        {
          provide: CAN_SHARE_WITH_ACCOUNT_GROUPS_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: canShareWithAccountGroupsFactory,
        },
        {
          provide: CAN_SHARE_TOKEN,
          deps: [CAN_SHARE_WITH_ACCOUNT_GROUPS_TOKEN, CAN_SHARE_WITH_PARTNERS_TOKEN],
          useFactory: canShareFactory,
        },
        {
          provide: PARTNER_NAME_RESOLVER_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: partnerNameResolverFactory,
        },
        {
          provide: ACCOUNT_GROUPS_LOOKUP_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: accountGroupsLookupFactory,
        },
        {
          provide: MULTI_LOCATION_GROUPS_LOOKUP_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: multiLocationGroupsLookupFactory,
        },
        {
          provide: PARTNERS_LOOKUP_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: partnersLookupFactory,
        },
      ],
    };
    return result;
  }

  constructor(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer) {
    iconRegistry.addSvgIconLiteral('ai-knowledge-icon', sanitizer.bypassSecurityTrustHtml(KnowledgeIcon));
  }
}

function accountGroupIdFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.accountGroupId$) ? config.accountGroupId$ : of(config.accountGroupId$);
}

function marketIdFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.marketId$) ? config.marketId$ : of(config.marketId$);
}

function partnerIdFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.partnerId$) ? config.partnerId$ : of(config.partnerId$);
}

function manageKnowledgeUrlFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.manageKnowledgeUrl$) ? config.manageKnowledgeUrl$ : of(config.manageKnowledgeUrl$);
}

function businessProfileUrlFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.businessProfileUrl$) ? config.businessProfileUrl$ : of(config.businessProfileUrl$);
}

function showBusinessProfileSourceFactory(config: AiKnowledgeConfig): Observable<boolean | undefined | null> {
  return isObservable(config.showBusinessProfileSource$)
    ? config.showBusinessProfileSource$
    : of(config.showBusinessProfileSource$);
}

function webChatWidgetEditRouteFactory(config: AiKnowledgeConfig): Observable<RouteCommand[] | undefined | null> {
  return isObservable(config.webChatWidgetEditRoute$)
    ? config.webChatWidgetEditRoute$
    : of(config.webChatWidgetEditRoute$);
}

function editingEnabledFactory(config: AiKnowledgeConfig): Observable<boolean> {
  if (config.editingEnabled$ == null) {
    return of(true);
  }
  return isObservable(config.editingEnabled$)
    ? (config.editingEnabled$ as Observable<boolean>)
    : of(config.editingEnabled$ as boolean);
}

// ─── Knowledge-sharing factories ───────────────────────────────

function knowledgeGrantsEnabledFactory(config: AiKnowledgeConfig): Observable<boolean> {
  // Default off keeps the existing KnowledgeSourceManagementComponent unchanged.
  if (config.knowledgeGrantsEnabled$ == null) {
    return of(false);
  }
  return isObservable(config.knowledgeGrantsEnabled$)
    ? (config.knowledgeGrantsEnabled$ as Observable<boolean>)
    : of(config.knowledgeGrantsEnabled$ as boolean);
}

function canShareWithPartnersFactory(config: AiKnowledgeConfig): Observable<boolean> {
  if (config.canShareWithPartners$ == null) {
    return of(false);
  }
  return isObservable(config.canShareWithPartners$)
    ? (config.canShareWithPartners$ as Observable<boolean>)
    : of(config.canShareWithPartners$ as boolean);
}

function canShareWithAccountGroupsFactory(config: AiKnowledgeConfig): Observable<boolean> {
  if (config.canShareWithAccountGroups$ == null) {
    return of(true);
  }
  return isObservable(config.canShareWithAccountGroups$)
    ? (config.canShareWithAccountGroups$ as Observable<boolean>)
    : of(config.canShareWithAccountGroups$ as boolean);
}

// canShare = either capability granted — drives whether the per-row "Share…" affordance shows at all.
function canShareFactory(
  canShareWithAccountGroups$: Observable<boolean>,
  canShareWithPartners$: Observable<boolean>,
): Observable<boolean> {
  return combineLatest([canShareWithAccountGroups$, canShareWithPartners$]).pipe(
    map(([withAccountGroups, withPartners]) => withAccountGroups || withPartners),
  );
}

function partnerNameResolverFactory(config: AiKnowledgeConfig): PartnerNameResolver {
  // Identity default surfaces missing wiring as a raw namespace id in the UI rather than failing silently.
  return config.partnerNameResolver ?? ((namespace) => of(namespace.id));
}

function accountGroupsLookupFactory(config: AiKnowledgeConfig): TargetLookup | null {
  return config.accountGroupsLookup ?? null;
}

function multiLocationGroupsLookupFactory(config: AiKnowledgeConfig): TargetLookup | null {
  return config.multiLocationGroupsLookup ?? null;
}

function partnersLookupFactory(config: AiKnowledgeConfig): TargetLookup | null {
  return config.partnersLookup ?? null;
}
